import { Component, Input } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CardModule,
  CheckboxModule,
  DropdownModule,
  RadioButtonModule,
  AlertModule,
  TooltipModule,
} from 'axis-components';
import { CommonModule, SlicePipe } from '@angular/common';
import { BaseRules, Control, TextValues } from './connecticut-model';
import { UimuiBaseStateComponent } from '../uimui-base-state.component';
import { TruncateModule } from 'axis-components/truncate';

@Component({
  selector: 'app-connecticut',
  standalone: true,
  imports: [
    CardModule,
    CheckboxModule,
    DropdownModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AlertModule,
    SlicePipe,
    CommonModule,
    TruncateModule,
    TooltipModule,
  ],
  templateUrl: './connecticut.component.html',
})
export class ConnecticutComponent extends UimuiBaseStateComponent<
  BaseRules,
  TextValues
> {
  @Input() set formGroup(form: FormGroup | null) {
    if (form) {
      super.getJsonRuleFiles('connecticut', form);
    }
  }
  @Input() set isReview(review: boolean) {
    if (review) {
      super.getJsonRuleFiles('connecticut', null);
    }
  }

  dynamicLimitId: Object = {
    UICUIM_CombinedSingleMinLimit_DropDown: 'uicuim_combinedsingleminlimit_50k',
    UICUIM_SplitMinLimits_DropDown: 'uicuim_splitminlimits_25k_50k',
    UISUIM_CombinedSingleMinLimit_DropDown: 'uisuim_combinedsingleminlimit_50k',
    UISUIM_SplitMinLimits_DropDown: 'uisuim_splitminlimits_25k_50k',
    UISUIM_LiabilityLimit_DropDown: 'uisuim_liabilitylimit',
    UICUIM_LiabilityLimit_DropDown: 'uicuim_liabilitylimit',
  };

  /**
   * Connecticut offers two UIM flavours (standard "UISUIM" and conversion
   * "UICUIM"), each with its own CSL premium table. When the insured selects
   * "Limit equal to the policy liability limit" / "...double the policy
   * liability limit" the premium must be looked up from the same flavour's CSL
   * dropdown.
   */
  override getLookupControlName(control: Control): string | undefined {
    const isConversion = control.formControlName.startsWith('UICUIM');

    return isConversion
      ? 'UICUIM_CombinedSingleLimit_DropDown'
      : 'UISUIM_CombinedSingleLimit_DropDown';
  }

  /**
   * When the insured chooses "Policy Lia limit" on the Select Coverage screen,
   * the readonly UISUIM_/UICUIM_ liability-limit fields must be filled with the
   * policy's liability limit and its calculated premium (BR4/BR5). Routing the
   * hook through setupHookForLiabilityDropDownData enables that calculation.
   */
  override setupHookForDropDownData(): void {
    this.setupHookForLiabilityDropDownData();
  }

  /**
   * Connecticut prints the "policy liability limit" and "double the policy
   * liability limit" premiums in separate cells of the disclosure, so each must
   * be sent under its own PDF key instead of the shared `liabilityPremiumUIM`.
   * The selected dropdown option (stored as the option object on the reactive
   * form) tells us which limit type the calculated premium belongs to.
   */
  override storePremiumInPdfDump(
    control: Control,
    premium: string,
    state: string,
  ): void {
    const selected = this.reactiveForm?.get(control.formControlName)?.value;
    const selectedName =
      typeof selected === 'string' ? selected : (selected?.name ?? '');
    const pdfKey = selectedName.includes('double the policy liability limit')
      ? 'liabilityPremiumUIMDouble'
      : 'liabilityPremiumUIM';

    this.baseRulesService.pdfDataDump[state] = {
      ...this.baseRulesService.pdfDataDump[state],
      [pdfKey]: premium,
    };
  }
}
